import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Notification, NotificationType } from '../types';
// FIX: Updated icon import path to use the 'shared-icons' package.
import { ShieldCheckIcon, ShieldExclamationIcon, InfoIcon, AlertTriangleIcon, XIcon } from 'shared-icons';

interface NotificationContextType {
  addNotification: (notification: Omit<Notification, 'id'>) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): ((notification: Omit<Notification, 'id'>) => void) => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context.addNotification;
};

interface ToastProps {
  notification: Notification;
  onDismiss: (id: number) => void;
}

const Toast: React.FC<ToastProps> = ({ notification, onDismiss }) => {
  const [isClosing, setIsClosing] = useState(false);

  const icons: { [key in NotificationType]: React.ReactNode } = {
    success: <ShieldCheckIcon className="w-6 h-6 text-green-500" />,
    error: <ShieldExclamationIcon className="w-6 h-6 text-red-500" />,
    info: <InfoIcon className="w-6 h-6 text-cyan-500" />,
    warning: <AlertTriangleIcon className="w-6 h-6 text-yellow-500" />,
  };

  const progressColors: { [key in NotificationType]: string } = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-cyan-500',
      warning: 'bg-yellow-500',
  };

  const duration = notification.duration ?? 5000;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsClosing(true);
      setTimeout(() => onDismiss(notification.id), 300); // Wait for animation
    }, duration);

    return () => clearTimeout(timer);
  }, [notification.id, onDismiss, duration]);

  const handleDismiss = () => {
    setIsClosing(true);
    setTimeout(() => onDismiss(notification.id), 300);
  };

  return (
    <div
      className={`toast ${isClosing ? 'toast-closing' : ''} w-full max-w-sm bg-white dark:bg-slate-800 shadow-2xl rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 dark:ring-white/10 overflow-hidden`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">{icons[notification.type]}</div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-bold text-slate-900 dark:text-white">{notification.title}</p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{notification.message}</p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={handleDismiss}
              className="inline-flex text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              aria-label="Close"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      <div className={`toast-progress h-1 ${progressColors[notification.type]}`} style={{ animationDuration: `${duration}ms` }} />
    </div>
  );
};

const NotificationContainer: React.FC<{ notifications: Notification[]; onDismiss: (id: number) => void }> = ({ notifications, onDismiss }) => {
  return (
    <div
      aria-live="polite"
      className="fixed inset-0 flex flex-col items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-end z-[100]"
    >
      <div className="w-full max-w-sm space-y-4">
        {notifications.map((notification) => (
          <Toast key={notification.id} notification={notification} onDismiss={onDismiss} />
        ))}
      </div>
    </div>
  );
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, ...notification }]);
  }, []);

  const removeNotification = useCallback((id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      {children}
      <NotificationContainer notifications={notifications} onDismiss={removeNotification} />
    </NotificationContext.Provider>
  );
};